import React from 'react';
import { useTranslation } from 'react-i18next';
import ClockIcon from '../../../../../assets/icons/clock-icon.svg';
import { CustomButton } from '../../../../../components/Button';
import SwipeButton from '../../../../../components/SwipeButton/SwipeButton';
import { LANG_TEXT } from '../../../../../constants/language';
import { useAuthContext } from '../../../../../context/authContext';
import { getOverallStatus } from '../../../../../utils/multiOrder.utils';
import { DamagedItemStatus, Order, OrderItemStatus } from '../../../order.types';

const ActionButton: React.FC<{
  item: Order;
  claimed: boolean;
  onPickUp: (item: Order, restaurantIds?: string[]) => void;
  onConfirmDelivered: (item: Order, restaurantIds?: string[]) => void;
  onConfirmPickedUp: (item: Order, restaurantIds?: string[]) => void;
}> = ({ item, claimed, onPickUp, onConfirmDelivered, onConfirmPickedUp }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();

  const myOrders = item.multi_orders?.filter((order) => order.rider_id === user?.uid);
  const totalStatus = getOverallStatus(myOrders || []);

  if (!item) {
    return null;
  }

  if (item?.multi_orders && item?.multi_orders.length > 1) {
    const availableRestaurantIds = item.multi_orders?.filter((order) => !order.rider_id).map((order) => order.restaurant_id) || [];

    if (item.multi_orders?.some((order) => !order.rider_id) && !claimed) {
      return (
        <CustomButton
          fullWidth
          backgroundColor="#4E5BA6"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onPickUp(item, availableRestaurantIds);
          }}
        >
          {t(LANG_TEXT.IN_CHARGE_OF_ALL_FOOD_TRUCKS)}
        </CustomButton>
      );
    }

    switch (totalStatus as OrderItemStatus | DamagedItemStatus | null) {
      case DamagedItemStatus.CANCELLED:
      case DamagedItemStatus.DAMAGED_CANCELLED: {
        return (
          <CustomButton fullWidth sx={{ color: '#B42318 !important' }} variant="outlined" disabled>
            {t(LANG_TEXT.CANCELLED)}
          </CustomButton>
        );
      }
      case OrderItemStatus.READY_FOR_PICK_UP:
      case OrderItemStatus.DAMAGED_READY_FOR_PICK_UP: {
        const myOrderRestaurantIds =
          item.multi_orders
            ?.filter(
              (order) =>
                order.rider_id === user?.uid && [OrderItemStatus.READY_FOR_PICK_UP, OrderItemStatus.DAMAGED_READY_FOR_PICK_UP].includes(order.status as OrderItemStatus)
            )
            .map((order) => order.restaurant_id) || [];

        return (
          <CustomButton
            fullWidth
            backgroundColor="#4E5BA6"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              onConfirmPickedUp(item, myOrderRestaurantIds);
            }}
          >
            {t(LANG_TEXT.PICKED_UP_FOOD)}
          </CustomButton>
        );
      }

      case OrderItemStatus.PREPARING:
      case OrderItemStatus.DAMAGED_PREPARING: {
        return (
          <CustomButton fullWidth variant="outlined" disabled startIcon={<img src={ClockIcon} width={17} height={17} alt="user" />}>
            {t(LANG_TEXT.PREPARING)}
          </CustomButton>
        );
      }

      case OrderItemStatus.DELIVERING:
      case OrderItemStatus.DAMAGED_DELIVERING: {
        return (
          <SwipeButton
            slideText={t(LANG_TEXT.SLIDE_TO_MARK_AS_DONE)}
            doneText={t(LANG_TEXT.DELIVERED)}
            onSuccess={() => {
              onConfirmDelivered(
                item,
                myOrders?.map((order) => order.restaurant_id)
              );
            }}
          />
        );
      }

      case OrderItemStatus.DELIVERED: {
        return (
          <CustomButton fullWidth variant="contained" disabled>
            {t(LANG_TEXT.DELIVERED)}
          </CustomButton>
        );
      }

      default: {
        return null;
      }
    }
  }

  if (!item?.rider_id)
    return (
      <CustomButton
        fullWidth
        variant="contained"
        onClick={(e) => {
          e.stopPropagation();
          onPickUp(item);
        }}
      >
        {t(LANG_TEXT.PICK_UP_ORDER)}
      </CustomButton>
    );
  switch (item?.order_status) {
    case OrderItemStatus.PREPARING:
    case OrderItemStatus.DAMAGED_PREPARING: {
      return (
        <CustomButton fullWidth variant="outlined" disabled startIcon={<img src={ClockIcon} width={17} height={17} alt="user" />}>
          {t(LANG_TEXT.PREPARING)}
        </CustomButton>
      );
    }
    case OrderItemStatus.DELIVERING:
    case OrderItemStatus.DAMAGED_DELIVERING: {
      return (
        <SwipeButton
          slideText={t(LANG_TEXT.SLIDE_TO_MARK_AS_DONE)}
          doneText={t(LANG_TEXT.DELIVERED)}
          onSuccess={() => {
            onConfirmDelivered(item);
          }}
        />
      );
    }
    case OrderItemStatus.READY_FOR_PICK_UP:
    case OrderItemStatus.DAMAGED_READY_FOR_PICK_UP: {
      return (
        <CustomButton
          fullWidth
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onConfirmPickedUp(item);
          }}
        >
          {t(LANG_TEXT.PICKED_UP_FOOD)}
        </CustomButton>
      );
    }
    case OrderItemStatus.DELIVERED: {
      return (
        <CustomButton
          fullWidth
          variant="contained"
          disabled
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {t(LANG_TEXT.DELIVERED)}
        </CustomButton>
      );
    }
    default: {
      return null;
    }
  }
};

export default ActionButton;
